import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Row,
  Col,
  message,
  Popconfirm,
  QRCode,
  Descriptions,
  Statistic,
  Divider,
  DatePicker,
  InputNumber
} from 'antd';
import {
  MobileOutlined,
  QrcodeOutlined,
  SendOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  BellOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface QRCodeItem {
  _id: string;
  code: string;
  url: string;
  type: string;
  status: string;
  usageCount: number;
  maxUsage?: number;
  expiresAt?: string;
  createdAt: string;
  station?: { name: string };
  campaign?: { title: string };
}

interface PushNotification {
  _id: string;
  title: string;
  body: string;
  category: string;
  status: string;
  sentAt?: string;
  createdAt: string;
}

const MobileIntegration: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [qrCodes, setQRCodes] = useState<QRCodeItem[]>([]);
  const [pushNotifications, setPushNotifications] = useState<PushNotification[]>([]);
  const [qrModalVisible, setQRModalVisible] = useState(false);
  const [pushModalVisible, setPushModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedQR, setSelectedQR] = useState<QRCodeItem | null>(null);
  const [form] = Form.useForm();
  const [pushForm] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchQRCodes();
    fetchPushHistory();
  }, []);

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      const response = await stationOwnerAPI.getQRCodes();
      if (response.success) {
        setQRCodes(response.qrCodes || []);
      }
    } catch (error: any) {
      message.error('QR kodlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchPushHistory = async () => {
    try {
      const response = await stationOwnerAPI.getPushHistory();
      if (response.success) {
        setPushNotifications(response.notifications || []);
      }
    } catch (error: any) {
      message.error('Push notification geçmişi yüklenemedi');
    }
  };

  const handleCreateQR = async (values: any) => {
    try {
      setLoading(true);
      const response = await stationOwnerAPI.createQRCode({
        type: values.type,
        stationId: values.stationId,
        campaignId: values.campaignId,
        data: {
          title: values.title,
          description: values.description,
          action: values.action
        },
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : undefined,
        maxUsage: values.maxUsage,
        settings: {
          size: values.size || 200,
          errorCorrectionLevel: values.errorCorrectionLevel || 'M'
        }
      });

      if (response.success) {
        message.success('QR kod başarıyla oluşturuldu');
        setQRModalVisible(false);
        form.resetFields();
        fetchQRCodes();
      }
    } catch (error: any) {
      message.error('QR kod oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPush = async (values: any) => {
    try {
      setLoading(true);
      const response = await stationOwnerAPI.sendPush({
        title: values.title,
        body: values.body,
        category: values.category,
        type: values.type,
        data: {
          customData: values.customData
        }
      });

      if (response.success) {
        message.success('Push notification başarıyla gönderildi');
        setPushModalVisible(false);
        pushForm.resetFields();
        fetchPushHistory();
      }
    } catch (error: any) {
      message.error('Push notification gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQR = async (id: string) => {
    try {
      setLoading(true);
      const response = await stationOwnerAPI.deleteQRCode(id);
      if (response.success) {
        message.success('QR kod başarıyla silindi');
        fetchQRCodes();
      }
    } catch (error: any) {
      message.error('QR kod silinemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (id: string) => {
    try {
      const response = await stationOwnerAPI.getQRCodeDetail(id);
      if (response.success) {
        setSelectedQR(response.qrCode);
        setDetailModalVisible(true);
      }
    } catch (error: any) {
      message.error('QR kod detayı yüklenemedi');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'green',
      inactive: 'default',
      expired: 'orange',
      used: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      active: 'Aktif',
      inactive: 'Pasif',
      expired: 'Süresi Doldu',
      used: 'Kullanıldı'
    };
    return texts[status] || status;
  };

  const qrColumns = [
    {
      title: 'Kod',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <Typography.Text code>{code.substring(0, 8)}...</Typography.Text>
    },
    {
      title: 'Tip',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => type.toUpperCase()
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      )
    },
    {
      title: 'Kullanım',
      key: 'usage',
      render: (_: any, record: QRCodeItem) => (
        <span>
          {record.usageCount} / {record.maxUsage || '∞'}
        </span>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: QRCodeItem) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record._id)}
          >
            Detay
          </Button>
          <Popconfirm
            title="QR kodu silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteQR(record._id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const pushColumns = [
    {
      title: 'Başlık',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => category.toUpperCase()
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'sent' ? 'green' : status === 'failed' ? 'red' : 'orange'}>
          {status === 'sent' ? 'Gönderildi' : status === 'failed' ? 'Başarısız' : 'Beklemede'}
        </Tag>
      )
    },
    {
      title: 'Tarih',
      dataIndex: 'sentAt',
      key: 'sentAt',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-'
    }
  ];

  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      <Title level={2} style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '24px' }}>
        <MobileOutlined /> Mobil Uygulama Entegrasyonu
      </Title>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Toplam QR Kod"
              value={qrCodes.length}
              prefix={<QrcodeOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Aktif QR Kod"
              value={qrCodes.filter(qr => qr.status === 'active').length}
              prefix={<QrcodeOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Toplam Push"
              value={pushNotifications.length}
              prefix={<BellOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Başarılı Push"
              value={pushNotifications.filter(p => p.status === 'sent').length}
              prefix={<SendOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <QrcodeOutlined />
                <span>QR Kodlar</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setQRModalVisible(true)}
              >
                Yeni QR Kod
              </Button>
            }
          >
            <Table
              columns={qrColumns}
              dataSource={qrCodes}
              loading={loading}
              rowKey="_id"
              pagination={{
                showSizeChanger: false,
                responsive: true
              }}
              scroll={{ x: true }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BellOutlined />
                <span>Push Notification Geçmişi</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => setPushModalVisible(true)}
              >
                Push Gönder
              </Button>
            }
          >
            <Table
              columns={pushColumns}
              dataSource={pushNotifications}
              loading={loading}
              rowKey="_id"
              pagination={{
                showSizeChanger: false,
                responsive: true,
                pageSize: 5
              }}
              scroll={{ x: true }}
            />
          </Card>
        </Col>
      </Row>

      {/* QR Kod Oluşturma Modal */}
      <Modal
        title="Yeni QR Kod Oluştur"
        open={qrModalVisible}
        onCancel={() => {
          setQRModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={isMobile ? '90%' : 600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateQR}>
          <Form.Item
            name="type"
            label="QR Kod Tipi"
            rules={[{ required: true, message: 'Tip seçiniz' }]}
          >
            <Select>
              <Option value="station">İstasyon</Option>
              <Option value="campaign">Kampanya</Option>
              <Option value="promotion">Promosyon</Option>
              <Option value="loyalty">Sadakat</Option>
              <Option value="custom">Özel</Option>
            </Select>
          </Form.Item>

          <Form.Item name="stationId" label="İstasyon (Opsiyonel)">
            <Select placeholder="İstasyon seçiniz" allowClear>
              {/* İstasyonlar buraya eklenecek */}
            </Select>
          </Form.Item>

          <Form.Item name="campaignId" label="Kampanya (Opsiyonel)">
            <Select placeholder="Kampanya seçiniz" allowClear>
              {/* Kampanyalar buraya eklenecek */}
            </Select>
          </Form.Item>

          <Form.Item name="title" label="Başlık">
            <Input placeholder="QR kod başlığı" />
          </Form.Item>

          <Form.Item name="description" label="Açıklama">
            <TextArea rows={3} placeholder="QR kod açıklaması" />
          </Form.Item>

          <Form.Item name="maxUsage" label="Maksimum Kullanım">
            <InputNumber min={1} placeholder="Sınırsız için boş bırakın" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="expiresAt" label="Son Kullanma Tarihi">
            <DatePicker style={{ width: '100%' }} showTime />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              QR Kod Oluştur
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Push Notification Modal */}
      <Modal
        title="Push Notification Gönder"
        open={pushModalVisible}
        onCancel={() => {
          setPushModalVisible(false);
          pushForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '90%' : 600}
      >
        <Form form={pushForm} layout="vertical" onFinish={handleSendPush}>
          <Form.Item
            name="title"
            label="Başlık"
            rules={[{ required: true, message: 'Başlık giriniz' }]}
          >
            <Input placeholder="Bildirim başlığı" />
          </Form.Item>

          <Form.Item
            name="body"
            label="İçerik"
            rules={[{ required: true, message: 'İçerik giriniz' }]}
          >
            <TextArea rows={4} placeholder="Bildirim içeriği" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Kategori"
            rules={[{ required: true, message: 'Kategori seçiniz' }]}
          >
            <Select>
              <Option value="system">Sistem</Option>
              <Option value="campaign">Kampanya</Option>
              <Option value="alert">Uyarı</Option>
              <Option value="payment">Ödeme</Option>
              <Option value="promotion">Promosyon</Option>
            </Select>
          </Form.Item>

          <Form.Item name="type" label="Tip" initialValue="info">
            <Select>
              <Option value="info">Bilgi</Option>
              <Option value="success">Başarı</Option>
              <Option value="warning">Uyarı</Option>
              <Option value="error">Hata</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Push Notification Gönder
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* QR Kod Detay Modal */}
      <Modal
        title="QR Kod Detayı"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedQR(null);
        }}
        footer={null}
        width={isMobile ? '90%' : 600}
      >
        {selectedQR && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <QRCode value={selectedQR.url} size={200} />
            </div>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Kod">{selectedQR.code}</Descriptions.Item>
              <Descriptions.Item label="URL">
                <a href={selectedQR.url} target="_blank" rel="noopener noreferrer">
                  {selectedQR.url}
                </a>
              </Descriptions.Item>
              <Descriptions.Item label="Tip">{selectedQR.type.toUpperCase()}</Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={getStatusColor(selectedQR.status)}>
                  {getStatusText(selectedQR.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Kullanım Sayısı">
                {selectedQR.usageCount} / {selectedQR.maxUsage || '∞'}
              </Descriptions.Item>
              {selectedQR.expiresAt && (
                <Descriptions.Item label="Son Kullanma">
                  {dayjs(selectedQR.expiresAt).format('DD.MM.YYYY HH:mm')}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Oluşturulma">
                {dayjs(selectedQR.createdAt).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>
    </div>
  );
};

export default MobileIntegration;

